import { AlertTriangle, TrendingUp, TrendingDown } from "lucide-react";
import {
  ChannelBreakdown,
  InventoryAlertItem,
  InventoryInsightItem,
  ProductInventoryData,
  RestaurantInventoryTotals,
} from "../redux/allocationService";

export const calculateRestaurantInventoryTotals = (
  inventoryProducts: ProductInventoryData[],
): RestaurantInventoryTotals => {
  const totals: RestaurantInventoryTotals = {
    totalProduction: inventoryProducts.reduce(
      (sum, product) => sum + product.production.total,
      0,
    ),
    totalAllocated: inventoryProducts.reduce(
      (sum, product) => sum + product.allocated,
      0,
    ),
    totalOrders: inventoryProducts.reduce(
      (sum, product) => sum + product.orders.advance + product.orders.sameDay,
      0,
    ),
    totalAvailable: inventoryProducts.reduce(
      (sum, product) => sum + product.available,
      0,
    ),
    channelTotals: {
      web: inventoryProducts.reduce(
        (sum, product) => sum + product.channels.web.allocated,
        0,
      ),
      walkin: inventoryProducts.reduce(
        (sum, product) => sum + product.channels.walkin.allocated,
        0,
      ),
      route: inventoryProducts.reduce(
        (sum, product) => sum + product.channels.route.allocated,
        0,
      ),
      webDelivery: inventoryProducts.reduce(
        (sum, product) => sum + product.channels.webDelivery.allocated,
        0,
      ),
      whatsapp: inventoryProducts.reduce(
        (sum, product) => sum + product.channels.whatsapp.allocated,
        0,
      ),
      webPickup: inventoryProducts.reduce(
        (sum, product) => sum + (product.channels.webPickup?.allocated || 0),
        0,
      ),
      onRoute: inventoryProducts.reduce(
        (sum, product) => sum + (product.channels.onRoute?.allocated || 0),
        0,
      ),
    },
  };
  return totals;
};
export const getInventoryAlerts = (
  inventoryProducts: ProductInventoryData[],
): InventoryAlertItem[] => {
  const alerts: InventoryAlertItem[] = [];

  inventoryProducts.forEach((product) => {
    Object.entries(product.carts).forEach(([cartId, cartData]) => {
      Object.entries(cartData.channels).forEach(([channel, channelData]) => {
        if (channelData.available < 3 && channelData.available >= 0) {
          alerts.push({
            type: "warning",
            product: product.name,
            cart: cartId.toUpperCase(),
            channel,
            available: channelData.available,
            message: `${cartId.toUpperCase()} ${channel} running low`,
          });
        }
      });
    });
  });

  return alerts;
};

export const getCriticalInventoryAlerts = (
  inventoryProducts: ProductInventoryData[],
): InventoryAlertItem[] => {
  const criticalAlerts: InventoryAlertItem[] = [];

  inventoryProducts.forEach((product) => {
    Object.entries(product.carts).forEach(([cartId, cartData]) => {
      Object.entries(cartData.channels).forEach(([channel, channelData]) => {
        if (channelData.available < 0) {
          criticalAlerts.push({
            type: "critical",
            product: product.name,
            cart: cartId.toUpperCase(),
            channel,
            available: channelData.available,
            message: `${cartId.toUpperCase()} ${channel} over-committed`,
          });
        }
      });
    });
  });

  return criticalAlerts;
};

export const getInventoryInsights = (
  inventoryProducts: ProductInventoryData[],
): InventoryInsightItem[] => {
  return [
    {
      type: "high",
      message:
        "Chicken Rice & Curry has high overall availability (45+ units web channel)",
      icon: TrendingUp,
      colorClass: "text-success",
    },
    {
      type: "low",
      message:
        "Fish Rice running low across multiple carts (SLIC, BASL route channels)",
      icon: TrendingDown,
      colorClass: "text-warning",
    },
    {
      type: "critical",
      message: "Steamed Rice over-committed by 10 units restaurant-wide",
      icon: AlertTriangle,
      colorClass: "text-danger",
    },
  ];
};

export const getAvailabilityStatusClass = (available: number): string => {
  if (available < 0) return "text-danger fw-bold";
  if (available < 3) return "text-warning fw-bold";
  if (available < 10) return "text-info fw-bold";
  return "text-success fw-bold";
};

export const extractTierFromName = (displayName: string): string => {
  if (displayName.includes("(Bronze)")) return "Bronze";
  if (displayName.includes("(Silver)")) return "Silver";
  if (displayName.includes("(Gold)")) return "Gold";
  return "";
};

export const getProductTierBadgeColor = (tier: string): string => {
  switch (tier.toLowerCase()) {
    case "bronze":
      return "warning";
    case "silver":
      return "secondary";
    case "gold":
      return "success";
    default:
      return "info";
  }
};

export const formatTime = (timeInput: any, useUTC: boolean = true): string => {
  try {
    let date: Date;

    if (
      timeInput &&
      typeof timeInput === "object" &&
      timeInput._seconds !== undefined
    ) {
      date = new Date(timeInput._seconds * 1000);
    }
    // Handle Firestore timestamp format with seconds
    else if (
      timeInput &&
      typeof timeInput === "object" &&
      timeInput.seconds !== undefined
    ) {
      date = new Date(timeInput.seconds * 1000);
    }
    // Handle ISO string
    else if (typeof timeInput === "string") {
      date = new Date(timeInput);
    } else {
      console.warn("Unexpected time format:", timeInput);
      return "00:00";
    }

    if (isNaN(date.getTime())) {
      console.warn("Invalid date from input:", timeInput);
      return "00:00";
    }

    // Choose between UTC or local time
    if (useUTC) {
      // Return UTC time (what was originally sent)
      const utcHours = date.getUTCHours().toString().padStart(2, "0");
      const utcMinutes = date.getUTCMinutes().toString().padStart(2, "0");
      return `${utcHours}:${utcMinutes}`;
    } else {
      // Return local time
      const localHours = date.getHours().toString().padStart(2, "0");
      const localMinutes = date.getMinutes().toString().padStart(2, "0");
      return `${localHours}:${localMinutes}`;
    }
  } catch (error) {
    console.error("Error formatting time:", timeInput, error);
    return "00:00";
  }
};

export const createProductUniqueKey = (
  itemId: string,
  portionSize?: string,
  classFilterTag?: string,
): string => {
 return itemId.toLowerCase();
};

export enum CartChannels {
  WebPickup = "WebPickup",
  WebDelivery = "WebDelivery",
  CallCenter = "CallCenter",
  WhatsApp = "WhatsApp",
  PickMeUber = "PickMeUber",
  Route = "Route",
  OnRoute = "OnRoute",
  WalkIn = "WalkIn",
}

export const isSelectedDateToday = (selectedDate: string): boolean => {
  const today = new Date();

  // Get today's local date
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth() + 1; // getMonth() returns 0-11
  const todayDay = today.getDate();

  // Parse the selected date
  const selectedDateObj = new Date(selectedDate);
  const selectedYear = selectedDateObj.getFullYear();
  const selectedMonth = selectedDateObj.getMonth() + 1;
  const selectedDay = selectedDateObj.getDate();

  return (
    todayYear === selectedYear &&
    todayMonth === selectedMonth &&
    todayDay === selectedDay
  );
};

export const standardizeChannelName = (channelName: string): string => {
  const lowerName = channelName.toLowerCase();

  switch (lowerName) {
    case "whatsapp":
      return CartChannels.WhatsApp;
    case "callcenter":
      return CartChannels.CallCenter;
    case "uber":
    case "pickme":
    case "pickme/ uber":
    case "pickmeuber":
      return CartChannels.PickMeUber;
    case "route":
      return CartChannels.Route;
    case "walkin":
      return CartChannels.WalkIn;
    case "webpickup":
      return CartChannels.WebPickup;
    case "webdelivery":
      return CartChannels.WebDelivery;
    case "onroute":
      return CartChannels.OnRoute;
    default:
      return channelName
        .split(/[^a-zA-Z0-9]+/)
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join("");
  }
};
export const toPascalCaseForApi = (channelName: string): string => {
  const lowerName = channelName.toLowerCase();

  switch (lowerName) {
    case "whatsapp":
      return CartChannels.WhatsApp;
    case "callcenter":
      return CartChannels.CallCenter;
    case "uber":
    case "pickme":
    case "pickme/ uber":
    case "pickmeuber":
      return CartChannels.PickMeUber;
    case "route":
      return CartChannels.Route;
    case "walkin":
      return CartChannels.WalkIn;
    case "webpickup":
      return CartChannels.WebPickup;
    case "webdelivery":
      return CartChannels.WebDelivery;
    case "onroute":
      return CartChannels.OnRoute;
    default:
      return channelName
        .split(/[^a-zA-Z0-9]+/)
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join("");
  }
};
export const formatDateForAPISimple = (dateString: string): string => {
  const date = new Date(dateString);
  date.setUTCHours(0, 0, 0, 0);
  const isoString = date.toISOString();
  return isoString.replace(/\.\d{3}Z$/, "Z");
};

export const deepCopyChannels = (
  channels: Record<string, Record<string, ChannelBreakdown>>,
) => {
  return JSON.parse(JSON.stringify(channels));
};

export const convertTo24Hour = (timeStr: string): string => {
  if (timeStr === "Invalid Date") return "99:99";

  const [time, modifier] = timeStr.split(" ");
  let [hours, minutes] = time.split(":").map(Number);

  if (modifier === "PM" && hours < 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;

  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};


  export const getChannelDisplayName = (channel: string): string => {
    switch (channel) {
      case CartChannels.WhatsApp:
        return "WApp";
      case CartChannels.WalkIn:
        return "Walk";
      case CartChannels.WebPickup:
        return "WebP";
      case CartChannels.WebDelivery:
        return "WebD";
      case CartChannels.OnRoute:
        return "OnRt";
      case CartChannels.CallCenter:
        return "Call";
      case CartChannels.PickMeUber:
        return "PickMe/ Uber";
      case CartChannels.Route:
        return "Route";
      default:
        return channel.substring(0, 4);
    }
  };